// Canvas 2D drawing primitives for the retro scene.

import { SPRITES } from './sprites.js';

export const FONT_PIXEL = '"Press Start 2P", "Courier New", monospace';
export const FONT_TERM = '"VT323", "Courier New", monospace';

export function font(px, family) {
  return `${px}px ${family}`;
}

export const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
export const lerp = (a, b, t) => a + (b - a) * t;
export const easeOut = t => 1 - Math.pow(1 - t, 3);

// Word-wrap text with the current ctx.font.
export function wrapText(ctx, text, maxWidth) {
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

// 8-bit frame: four edge bars with the corners notched out.
export function drawPixelFrame(ctx, x, y, w, h, { color = '#fff', width = 3, notch = 6, dashed = false } = {}) {
  ctx.fillStyle = color;
  const seg = (sx, sy, sw, sh, horizontal) => {
    if (!dashed) {
      ctx.fillRect(sx, sy, sw, sh);
      return;
    }
    const dash = 10;
    const gap = 6;
    if (horizontal) {
      for (let i = 0; i < sw; i += dash + gap) ctx.fillRect(sx + i, sy, Math.min(dash, sw - i), sh);
    } else {
      for (let i = 0; i < sh; i += dash + gap) ctx.fillRect(sx, sy + i, sw, Math.min(dash, sh - i));
    }
  };
  seg(x + notch, y, w - notch * 2, width, true);
  seg(x + notch, y + h - width, w - notch * 2, width, true);
  seg(x, y + notch, width, h - notch * 2, false);
  seg(x + w - width, y + notch, width, h - notch * 2, false);
  // corner pixels
  ctx.fillRect(x + width, y + width, width, width);
  ctx.fillRect(x + w - width * 2, y + width, width, width);
  ctx.fillRect(x + width, y + h - width * 2, width, width);
  ctx.fillRect(x + w - width * 2, y + h - width * 2, width, width);
}

export function drawSprite(ctx, key, x, y, px, color = '#fff') {
  const rows = SPRITES[key];
  if (!rows) return;
  ctx.fillStyle = color;
  for (let r = 0; r < rows.length; r++) {
    const row = rows[r];
    for (let c = 0; c < row.length; c++) {
      if (row[c] === '#') ctx.fillRect(x + c * px, y + r * px, px, px);
    }
  }
}

export function spriteSize(key, px) {
  const rows = SPRITES[key] || [];
  return { w: (rows[0] ? rows[0].length : 0) * px, h: rows.length * px };
}

// Arcade push button. `hover` and `pressed` are 0..1.
export function drawArcadeButton(ctx, { x, y, w, h, label, hover = 0, pressed = 0, fontSize = 14 }) {
  const lift = -2 * hover + 6 * pressed;
  const shadow = 6 - 4 * pressed + 2 * hover;
  ctx.save();
  ctx.translate(0, lift);
  // shadow block
  ctx.fillStyle = '#1a1a1a';
  ctx.fillRect(x, y + shadow, w, h);
  // body
  const r = Math.round(45 + 210 * hover);
  ctx.fillStyle = `rgb(${r},${r},${r})`;
  ctx.fillRect(x, y, w, h);
  // 1px pixel highlight on top edge
  ctx.fillStyle = hover > 0.5 ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.08)';
  ctx.fillRect(x, y, w, 2);
  // label
  ctx.font = font(fontSize, FONT_PIXEL);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const c = Math.round(255 * (1 - hover));
  ctx.fillStyle = `rgb(${c},${c},${c})`;
  ctx.fillText(label, x + w / 2, y + h / 2 + 1);
  ctx.restore();
}

// Small bordered tag. Returns its width.
export function drawChip(ctx, text, x, y, { fontSize = 8, alpha = 0.8 } = {}) {
  ctx.font = font(fontSize, FONT_PIXEL);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  const padX = 8;
  const h = fontSize + 12;
  const w = Math.ceil(ctx.measureText(text).width) + padX * 2;
  ctx.fillStyle = `rgba(255,255,255,${alpha * 0.55})`;
  ctx.fillRect(x, y, w, 2);
  ctx.fillRect(x, y + h - 2, w, 2);
  ctx.fillRect(x, y, 2, h);
  ctx.fillRect(x + w - 2, y, 2, h);
  ctx.fillStyle = `rgba(255,255,255,${alpha})`;
  ctx.fillText(text, x + padX, y + h / 2 + 1);
  return w;
}
